import React from 'react';
import {
  List,
  TopToolbar,
  CreateButton,
  useRedirect,
  useListContext,
} from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip
} from '@mui/material';
import {
  Store as StoreIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { CustomEmptyState } from '../../components/CustomEmptyState';
import { PageTitle } from '../../components/PageTitle';

const ListActions = () => (
  <TopToolbar>
    <CreateButton
      variant="contained"
      startIcon={<AddIcon />}
    />
  </TopToolbar>
);

const BrandCard = ({ record, onClick }: { record: any; onClick: () => void }) => {
  const primaryColor = record.branding?.primaryColor || '#1976d2';

  return (
    <Card
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: 2,
        },
        borderLeft: `4px solid ${primaryColor}`,
        borderRadius: 0,
        minHeight: 'auto',
        mb: 1,
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '400px 200px 1fr 250px',
          gap: 2,
          alignItems: 'center',
          width: '100%'
        }}>
          {/* Column 1: Avatar and Name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={record.branding?.logoUrl}
              sx={{
                bgcolor: primaryColor,
                width: 48,
                height: 48
              }}
            >
              <StoreIcon sx={{ fontSize: 24 }} />
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="subtitle1"
                component="h3"
                sx={{
                  fontWeight: 600,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {record.name}
              </Typography>
              {record.enterprise?.name && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.85rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {record.enterprise.name}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Column 2: Industry */}
          <Box>
            {record.industry && (
              <Chip
                label={record.industry}
                size="small"
                sx={{
                  bgcolor: 'grey.100',
                  fontWeight: 500,
                  height: 24,
                  fontSize: '0.7rem'
                }}
              />
            )}
          </Box>

          {/* Column 3: Stats */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {record._count && (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Regions
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {record._count.regions || 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Franchisees
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {record._count.tenants || 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Workflows
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {record._count.workflows || 0}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Column 4: Settings Preview */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {record.settings?.defaultRoyaltyRate !== null && record.settings?.defaultRoyaltyRate !== undefined && (
              <Chip
                label={`${(record.settings.defaultRoyaltyRate * 100).toFixed(1)}% royalty`}
                size="small"
                sx={{
                  bgcolor: 'success.50',
                  color: 'success.main',
                  fontWeight: 500,
                  height: 24,
                  fontSize: '0.7rem'
                }}
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const BrandListHeader = () => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: '400px 200px 1fr 250px',
    gap: 2,
    alignItems: 'center',
    px: 2,
    py: 1.5,
    backgroundColor: 'grey.50',
    borderRadius: 0,
    mb: 2,
    border: '1px solid',
    borderColor: 'grey.200'
  }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Brand Name
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Industry
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
      Statistics
    </Typography>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary', textAlign: 'right' }}>
      Settings
    </Typography>
  </Box>
);

const BrandCardList = () => {
  const { data, isLoading } = useListContext();
  const redirect = useRedirect();

  if (isLoading) {
    return <Box>Loading...</Box>;
  }

  if (!data || data.length === 0) {
    return null;
  }

  return (
    <Box>
      <BrandListHeader />
      {data.map((record: any) => (
        <BrandCard
          key={record.id}
          record={record}
          onClick={() => redirect(`/brands/${record.id}/show`)}
        />
      ))}
    </Box>
  );
};

export const BrandList = () => {
  return (
    <Box>
      <PageTitle
        title="Brands"
        titleKey="pages.brands"
        subtitle="Manage franchise brands"
        icon={<StoreIcon />}
      />
      <List
        actions={<ListActions />}
        perPage={25}
        sort={{ field: 'name', order: 'ASC' }}
        empty={<CustomEmptyState resource="brands" />}
        sx={{
          '& .RaList-main': { mt: 0 },
          '& .RaList-content': { p: 2 }
        }}
      >
        <BrandCardList />
      </List>
    </Box>
  );
};
